import { useApi } from "@/hooks/use-api";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { useHotkeys } from "react-hotkeys-hook";
import { TextEditor } from "@/components/TextEditor";
import { Badge } from "@/components/ui/badge";
import { ArrowRightLeft } from "lucide-react";
import { useState } from "react";

type ScratchpadMode = "Fleeting" | "Permanent";

export const ScratchPad = () => {
  const currentDocument = useAppState((state) => state.currentDocument);
  const isScratchpadOpen = useAppState((state) => state.isScratchpadOpen);
  const user = useAppState((state) => state.user);
  const { updateDocument, updateUserNotes } = useApi();
  const dispatch = useAppDispatch();
  const [mode, setMode] = useState<ScratchpadMode>("Fleeting");
  const [isHovered, setIsHovered] = useState(false);

  useHotkeys(
    "mod+d",
    () => {
      dispatch({ type: "TOGGLE_SCRATCHPAD" });
    },
    { enableOnFormTags: true, preventDefault: true },
    [isScratchpadOpen]
  );

  useHotkeys(
    "esc",
    () => {
      if (isScratchpadOpen) {
        dispatch({ type: "TOGGLE_SCRATCHPAD" });
      }
    },
    { enableOnFormTags: true },
    [isScratchpadOpen]
  );

  const handleSave = (text: string) => {
    // Fire and forget - API methods handle optimistic updates internally
    // User's scratchpad is the source of truth, so we don't await
    if (mode === "Fleeting") {
      updateDocument(currentDocument!.id, { notes: text });
    } else {
      updateUserNotes(text);
    }
  };

  const toggleMode = () => {
    setMode((prevMode) => (prevMode === "Fleeting" ? "Permanent" : "Fleeting"));
  };

  if (!isScratchpadOpen) return null;

  const content =
    mode === "Fleeting" ? currentDocument?.notes || "" : user?.notes || "";

  return (
    <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 h-[300px] w-[600px] bg-zinc-50 border rounded-lg shadow-xs">
      <TextEditor
        key={mode}
        autosave
        scrollable
        className="h-[250px]! border-none shadow-none bg-transparent font-ibm-plex-mono text-[14px]! p-6! pb-[40px]! text-zinc-600"
        placeholder="This is your scratchpad. Type here to add thoughts and reminders you want to keep private ..."
        content={content}
        onContentChange={() => {}}
        onSave={handleSave}
      />

      <div
        className="absolute bottom-0 left-4 right-4 h-[50px] text-xs bg-zinc-50 z-99"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-center h-full">
          <Badge
            className="cursor-pointer transition-all duration-500 ease-in-out overflow-hidden"
            variant={mode === "Fleeting" ? "outline" : "default"}
            onClick={toggleMode}
          >
            <span className="flex items-center gap-1.5">
              {isHovered && (
                <ArrowRightLeft className="h-3.5 w-3.5 animate-in fade-in slide-in-from-left-1 duration-500" />
              )}
              <span className="transition-all duration-500">
                {isHovered ? mode : mode.charAt(0)}
              </span>
            </span>
          </Badge>

          {isHovered && (
            <div className="ml-auto mr-2">
              <Badge
                variant="secondary"
                className="text-secondary-foreground mr-2"
              >
                ESC
              </Badge>
              <span className="text-muted-foreground">to close</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
